/**
 * Role Permissions Page
 * Enterprise-grade "Who Can Do What" permission matrix.
 * 
 * Design Patterns:
 * - Amazon IAM: Policy-based permission visualization
 * - Google Admin Console: Interactive checkbox matrix
 * - AS9100D: Complete audit trail for permission changes
 * 
 * Features:
 * - Interactive checkbox grid (8 roles × 81 capabilities)
 * - Category grouping with expand/collapse
 * - Batch save with unsaved changes indicator
 * - Reset to defaults functionality
 * - Search/filter capabilities
 * - Role statistics cards
 * 
 * @author Enterprise User Management Module
 * @version 1.0.0
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Shield, Save, RotateCcw, Search, ChevronDown, ChevronRight,
  Check, X, Users, Settings, FileText, Package, FolderTree,
  Warehouse, CheckCircle, BarChart, Server, Loader2, AlertTriangle,
  Info, Lock, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

import {
  getPermissionMatrix,
  updateRolePermissions,
  resetAllPermissions,
  ROLE_COLORS,
  CATEGORY_LABELS
} from '../services/permissionService';

// =============================================================================
// CONSTANTS
// =============================================================================

const CATEGORY_ICONS = {
  users: Users,
  roles: Shield,
  settings: Settings,
  audit: FileText,
  parts: Package,
  taxonomy: FolderTree,
  inventory: Warehouse,
  quality: CheckCircle,
  reports: BarChart,
  system: Server
};

const ROLE_COLOR_CLASSES = {
  red: 'bg-red-100 text-red-800 border-red-200',
  purple: 'bg-purple-100 text-purple-800 border-purple-200',
  blue: 'bg-blue-100 text-blue-800 border-blue-200',
  emerald: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  teal: 'bg-teal-100 text-teal-800 border-teal-200',
  amber: 'bg-amber-100 text-amber-800 border-amber-200',
  orange: 'bg-orange-100 text-orange-800 border-orange-200',
  slate: 'bg-slate-100 text-slate-800 border-slate-200'
};

const ROLE_NAMES = {
  admin: 'Admin',
  designs_manager: 'Design Mgr',
  designs_engineer: 'Design Eng',
  quality_manager: 'Quality Mgr',
  quality_engineer: 'Quality Eng',
  inventory_manager: 'Inv Mgr',
  operator: 'Operator',
  viewer: 'Viewer'
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

const RolePermissionsPage = () => {
  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [matrixData, setMatrixData] = useState(null);
  const [localMatrix, setLocalMatrix] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [changedRoles, setChangedRoles] = useState(new Set());
  
  // Load matrix data
  const loadMatrix = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getPermissionMatrix();
      setMatrixData(data);
      setLocalMatrix(data.matrix);
      
      // Expand all categories by default
      const expanded = {};
      Object.keys(data.permissions_by_category).forEach(cat => {
        expanded[cat] = true;
      });
      setExpandedCategories(expanded);
      
      setHasChanges(false);
      setChangedRoles(new Set());
    } catch (err) {
      console.error('Failed to load permission matrix:', err);
      toast.error('Failed to load permissions');
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    loadMatrix();
  }, [loadMatrix]);
  
  // Toggle permission for a role
  const togglePermission = useCallback((role, permissionCode) => {
    // Admin role cannot be modified
    if (role === 'admin') {
      toast.info('Admin role always has all permissions');
      return;
    }
    
    setLocalMatrix(prev => {
      const rolePerms = prev[role] || [];
      const newPerms = rolePerms.includes(permissionCode)
        ? rolePerms.filter(p => p !== permissionCode)
        : [...rolePerms, permissionCode];
      
      return { ...prev, [role]: newPerms };
    });
    
    setChangedRoles(prev => new Set([...prev, role]));
    setHasChanges(true);
  }, []);
  
  // Toggle category expand/collapse
  const toggleCategory = useCallback((category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  }, []);
  
  // Save changes
  const handleSave = async () => {
    if (!hasChanges || changedRoles.size === 0) return;
    
    setSaving(true);
    try {
      // Update each changed role
      const promises = Array.from(changedRoles).map(role =>
        updateRolePermissions(role, localMatrix[role] || [])
      );
      
      await Promise.all(promises);
      
      toast.success(`Updated permissions for ${changedRoles.size} role(s)`);
      setHasChanges(false);
      setChangedRoles(new Set());
      
      // Reload to get fresh data
      await loadMatrix();
    } catch (err) {
      console.error('Failed to save permissions:', err);
      toast.error('Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };
  
  // Reset all to defaults
  const handleResetAll = async () => {
    setSaving(true);
    try {
      await resetAllPermissions();
      toast.success('All permissions reset to defaults');
      setShowResetDialog(false);
      await loadMatrix();
    } catch (err) {
      console.error('Failed to reset permissions:', err);
      toast.error('Failed to reset permissions');
    } finally {
      setSaving(false);
    }
  };
  
  // Discard changes
  const handleDiscard = () => {
    if (matrixData) {
      setLocalMatrix(matrixData.matrix);
      setHasChanges(false);
      setChangedRoles(new Set());
      toast.info('Changes discarded');
    }
  };
  
  // Filter permissions by search term
  const filteredPermissionsByCategory = useMemo(() => {
    if (!matrixData) return {};
    
    const searchLower = searchTerm.toLowerCase();
    const result = {};
    
    Object.entries(matrixData.permissions_by_category).forEach(([cat, perms]) => {
      const filtered = perms.filter(p =>
        p.name.toLowerCase().includes(searchLower) ||
        p.code.toLowerCase().includes(searchLower) ||
        p.description.toLowerCase().includes(searchLower)
      );
      if (filtered.length > 0) {
        result[cat] = filtered;
      }
    });
    
    return result;
  }, [matrixData, searchTerm]);
  
  // Calculate permission counts for each role
  const permissionCounts = useMemo(() => {
    const counts = {};
    if (matrixData) {
      matrixData.roles.forEach(role => {
        counts[role] = (localMatrix[role] || []).length;
      });
    }
    return counts;
  }, [matrixData, localMatrix]);
  
  // Loading state
  if (loading) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#101957] mx-auto animate-spin mb-4" />
          <p className="text-slate-500">Loading permission matrix...</p>
        </div>
      </div>
    );
  }
  
  if (!matrixData) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <p className="text-slate-600">Failed to load permission matrix</p>
          <Button onClick={loadMatrix} className="mt-4">Retry</Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="p-6 space-y-6" data-testid="role-permissions-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Shield className="w-7 h-7 text-[#101957]" />
            Role Permissions
          </h1>
          <p className="text-slate-500 mt-1">
            Configure which roles have access to which capabilities
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {hasChanges && (
            <span className="text-sm text-amber-600 flex items-center gap-1">
              <AlertTriangle className="w-4 h-4" />
              Unsaved changes ({changedRoles.size} role{changedRoles.size !== 1 ? 's' : ''})
            </span>
          )}
          
          <Button
            variant="outline"
            onClick={handleDiscard}
            disabled={!hasChanges || saving}
          >
            <X className="w-4 h-4 mr-2" />
            Discard
          </Button>
          
          <Button
            variant="outline"
            onClick={() => setShowResetDialog(true)}
            disabled={saving}
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Reset All
          </Button>
          
          <Button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className="bg-[#101957] hover:bg-[#101957]/90 text-white"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Changes
          </Button>
        </div>
      </div>
      
      {/* Role Statistics Cards */}
      <div className="grid grid-cols-4 lg:grid-cols-8 gap-3">
        {matrixData.roles.map(role => {
          const color = ROLE_COLORS[role];
          const count = permissionCounts[role];
          const isChanged = changedRoles.has(role);
          
          return (
            <Card 
              key={role}
              className={`p-3 text-center relative ${isChanged ? 'ring-2 ring-amber-400' : ''}`}
            >
              {isChanged && (
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-amber-400 rounded-full" />
              )}
              <div className={`inline-flex px-2 py-1 rounded text-xs font-medium ${ROLE_COLOR_CLASSES[color]} mb-2`}>
                {ROLE_NAMES[role]}
              </div>
              <p className="text-2xl font-bold text-slate-900">{count}</p>
              <p className="text-xs text-slate-500">permissions</p>
            </Card>
          );
        })}
      </div>
      
      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          type="text"
          placeholder="Search permissions..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>
      
      {/* Permission Matrix */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            {/* Table Header */}
            <thead className="bg-slate-50 sticky top-0 z-10">
              <tr>
                <th className="text-left p-3 border-b border-slate-200 min-w-[300px]">
                  <span className="text-sm font-semibold text-slate-700">Permission</span>
                </th>
                {matrixData.roles.map(role => (
                  <th key={role} className="p-3 border-b border-slate-200 text-center min-w-[80px]">
                    <div className={`inline-flex px-2 py-1 rounded text-xs font-medium ${ROLE_COLOR_CLASSES[ROLE_COLORS[role]]}`}>
                      {ROLE_NAMES[role]}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            
            {/* Table Body - Grouped by Category */}
            <tbody>
              {Object.entries(filteredPermissionsByCategory).map(([category, permissions]) => {
                const CategoryIcon = CATEGORY_ICONS[category] || Settings;
                const isExpanded = expandedCategories[category];
                
                return (
                  <React.Fragment key={category}>
                    {/* Category Header Row */}
                    <tr 
                      className="bg-slate-100 cursor-pointer hover:bg-slate-200 transition-colors"
                      onClick={() => toggleCategory(category)}
                    >
                      <td colSpan={matrixData.roles.length + 1} className="p-3 border-b border-slate-200">
                        <div className="flex items-center gap-2">
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4 text-slate-500" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-slate-500" />
                          )}
                          <CategoryIcon className="w-4 h-4 text-slate-600" />
                          <span className="font-medium text-slate-800">
                            {CATEGORY_LABELS[category] || category}
                          </span>
                          <span className="text-sm text-slate-500">
                            ({permissions.length} permissions)
                          </span>
                        </div>
                      </td>
                    </tr>
                    
                    {/* Permission Rows */}
                    {isExpanded && permissions.map(permission => (
                      <tr 
                        key={permission.code}
                        className="hover:bg-slate-50 transition-colors"
                      >
                        <td className="p-3 border-b border-slate-100">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex items-center gap-2 cursor-help">
                                  {permission.is_sensitive && (
                                    <Lock className="w-3 h-3 text-amber-500" />
                                  )}
                                  <span className="text-sm text-slate-700">{permission.name}</span>
                                  <Info className="w-3 h-3 text-slate-400" />
                                </div>
                              </TooltipTrigger>
                              <TooltipContent side="right" className="max-w-xs">
                                <p className="font-medium">{permission.name}</p>
                                <p className="text-xs text-slate-400 mt-1">{permission.code}</p>
                                <p className="text-sm mt-2">{permission.description}</p>
                                {permission.is_sensitive && (
                                  <p className="text-xs text-amber-500 mt-2 flex items-center gap-1">
                                    <Lock className="w-3 h-3" />
                                    Sensitive permission
                                  </p>
                                )}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </td>
                        
                        {matrixData.roles.map(role => {
                          const hasPermission = (localMatrix[role] || []).includes(permission.code);
                          const isAdmin = role === 'admin';
                          
                          return (
                            <td key={role} className="p-3 border-b border-slate-100 text-center">
                              {isAdmin ? (
                                // Admin always has all permissions - show locked check
                                <div className="flex justify-center">
                                  <div className="w-5 h-5 rounded bg-green-100 flex items-center justify-center">
                                    <Check className="w-3.5 h-3.5 text-green-600" />
                                  </div>
                                </div>
                              ) : (
                                <Checkbox
                                  checked={hasPermission}
                                  onCheckedChange={() => togglePermission(role, permission.code)}
                                  className={hasPermission ? 'bg-[#101957] border-[#101957]' : ''}
                                />
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {/* Empty State */}
        {Object.keys(filteredPermissionsByCategory).length === 0 && (
          <div className="p-12 text-center">
            <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No permissions match your search</p>
          </div>
        )}
      </Card>
      
      {/* Legend */}
      <Card className="p-4">
        <h3 className="text-sm font-medium text-slate-700 mb-3">Legend</h3>
        <div className="flex flex-wrap gap-6 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded bg-green-100 flex items-center justify-center">
              <Check className="w-3.5 h-3.5 text-green-600" />
            </div>
            <span className="text-slate-600">Always enabled (Admin)</span>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox checked className="bg-[#101957] border-[#101957]" disabled />
            <span className="text-slate-600">Enabled</span>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox checked={false} disabled />
            <span className="text-slate-600">Disabled</span>
          </div>
          <div className="flex items-center gap-2">
            <Lock className="w-4 h-4 text-amber-500" />
            <span className="text-slate-600">Sensitive permission</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-amber-400 rounded-full" />
            <span className="text-slate-600">Role has unsaved changes</span>
          </div>
        </div>
      </Card>
      
      {/* Reset Confirmation Dialog */}
      <AlertDialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Reset All Permissions?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will reset ALL role permissions to their default values. 
              Any customizations you have made will be lost. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleResetAll}
              disabled={saving}
              className="bg-red-600 hover:bg-red-700"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RotateCcw className="w-4 h-4 mr-2" />
              )}
              Reset All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default RolePermissionsPage;
